// Grounded histogram — bars = value buckets, height = count. Interaction, axis and scale come from the
// shared shell (`SliderShell` + `useThresholdSlider`); this file owns the BODY (the bars) and the
// counting rules that only a measured histogram can apply.
//
// Bars are positioned by VALUE, not by index. A `flex-1` row only lines up with the value-positioned
// handle and highlight while the bins are exactly equal-width, and nothing on the wire enforces that —
// see `lib/slider-geometry.ts`.

import { cn } from '@/_shared/lib/utils';

import type { ConditionValue } from '../types';
import { useThresholdSlider } from './hooks/useThresholdSlider';
import { InsightSummary } from './InsightSummary';
import type { AxisScaleKind } from './lib/axis-scale';
import { pointOperatorBound } from './lib/bucket-bound';
import type { DistributionDomain } from './lib/distribution-domains';
import { formatCount } from './lib/format';
import { logTickValues } from './lib/log-ticks';
import { densityBarBoxes, HISTOGRAM_MIN_BAR_PCT } from './lib/slider-geometry';
import { POINT_OPS } from './lib/slider-ops';
import { SliderShell, SpanFill } from './SliderShell';
import type { DistributionBin, DistributionStats } from './types';

const BINS_INTERPOLATED = 'These buckets are interpolated from table statistics, not measured.';

/**
 * Whether this column's values are whole numbers, judged ONLY on numbers a warehouse actually returned —
 * the frame's own ends (which the probe is given as p01/p99) and each level of the ladder.
 *
 * Interior bin edges are excluded on purpose: they are computed as `low + i * (high - low) / 24`, so they
 * come out fractional even on a whole-number column, and inferring from them made every integer column
 * look decimal and commit thresholds like `order_count >= 4.2`.
 *
 * With no ladder the two ends are the only evidence there is, and they are kept: guessing WRONG in this
 * direction only widens the step on a decimal column, which the number input beside the chart can still
 * reach, whereas guessing the other way commits a fractional threshold on an integer column.
 */
function isIntegerValued(stats: DistributionStats | undefined, min: number, max: number): boolean {
  const { max: statsMax, min: statsMin, p01, p25, p50, p75, p95, p99 } = stats ?? {};
  return [min, max, statsMin, p25, p50, p75, p95, statsMax, p01, p99]
    .filter((v): v is number => v !== undefined)
    .every((v) => Number.isInteger(v));
}

/** Spread an optional end name without publishing `name: undefined` under `exactOptionalPropertyTypes`. */
const optName = (name: string | undefined) => (name === undefined ? {} : { name });

export function NumericDistribution({
  bins,
  stats,
  total,
  step,
  scale = 'linear',
  domain,
  operator,
  value,
  onPatch,
  ariaLabel,
  estimated = false,
  disabled = false,
}: {
  bins: DistributionBin[];
  stats?: DistributionStats;
  total?: number;
  step?: number;
  /** Which space the bins are equal-width in. MUST match how they were binned — see `axis-scale.ts`. */
  scale?: AxisScaleKind;
  domain: DistributionDomain; // reads/writes the predicate value, formats labels, and maps the operator
  operator: string;
  value: ConditionValue;
  onPatch: (patch: ConditionValue) => void;
  ariaLabel: string;
  /** The BINS themselves were interpolated from catalog statistics rather than measured. */
  estimated?: boolean;
  disabled?: boolean;
}) {
  const first = bins[0];
  const last = bins[bins.length - 1];
  // Positioning space is the BIN extent, not stats.min/max: the bars, handle and axis all map through
  // the same extent, and stats bounds inside the outer bins would desync them and make the edge bins
  // unreachable. Fallbacks keep the hook call unconditional; the empty case returns below.
  const min = first?.start ?? 0;
  const max = last?.end ?? 1;

  const slider = useThresholdSlider({
    min,
    max,
    step,
    scale,
    integerValued: isIntegerValued(stats, min, max),
    // No `single` when there is no p50: the hook parks it at the midpoint of the AXIS, which on a log
    // frame is a very different number from `(min + max) / 2` and the only one that reads as "middle".
    fallback: { ...(stats ? { single: stats.p50 } : {}), lo: stats?.p25 ?? min, hi: stats?.p75 ?? max },
    domain,
    operator,
    value,
    onPatch,
    ariaLabel,
    disabled,
  });

  if (!first || !last) return null;

  const { clampValue, committed, op, pct, single } = slider;
  const isPointOp = POINT_OPS.has(op);

  // Fraction of each bin's VALUE range a CONTINUOUS operator selects (0..1). The boundary bin is cut at
  // the handle so the count and the bar fill track it smoothly instead of flipping a whole bucket at the
  // midpoint. eq/neq never reach here — they cannot be expressed as a fraction of a bucket.
  //
  // VALUE space, deliberately, and it is the one place on a LOG axis where the coloured area and the
  // quoted count disagree (ACT2-569): the fill is clipped at `pct(single)`, the handle's true screen
  // position, and on a log axis a bin spans a ~1.4x ratio so screen fraction != value fraction. The
  // count is the number that must stay correct, and rows-uniform-inside-a-bucket is the honest
  // assumption for it — the same one `pg_stats` and `share-estimate.ts` make. Moving the fill to match
  // would put the handle line somewhere other than the value it holds, which is the worse lie. One bin
  // of 24, and only while a handle sits inside it.
  const clamp01 = (x: number) => Math.min(1, Math.max(0, x));
  const activeFraction = (bin: DistributionBin): number => {
    const w = bin.end - bin.start || 1;
    switch (op) {
      case 'gt':
      case 'gte':
        return clamp01((bin.end - single) / w);
      case 'lt':
      case 'lte':
        return clamp01((single - bin.start) / w);
      case 'btw':
        return clamp01((Math.min(bin.end, slider.hi) - Math.max(bin.start, slider.lo)) / w);
      case 'nbtw':
        return 1 - clamp01((Math.min(bin.end, slider.hi) - Math.max(bin.start, slider.lo)) / w);
      default:
        return 0;
    }
  };

  const binsTotal = bins.reduce((sum, bin) => sum + bin.count, 0);
  // The COMMITTED value, not the clamped handle position. A rule for a value outside the charted frame
  // clamps its handle to the edge, and looking the bucket up from that made the edge bucket answer for a
  // value it does not contain — `= 2019-01-01` against a 2023 frame quoted 4,000 rows, where the truth
  // is none. Unclamped, no bucket matches and the "outside the charted range" branch does its job.
  const holdsValue = (bin: DistributionBin, v: number): boolean =>
    bin.start <= v && (v < bin.end || (bin === last && v === bin.end));
  const committedSingle = slider.raw.single;
  const point = isPointOp
    ? pointOperatorBound({
        op: op as 'eq' | 'neq',
        bin: committedSingle === null ? undefined : bins.find((bin) => holdsValue(bin, committedSingle)),
        total,
        binsTotal,
        ...(step !== undefined || domain.granularity !== undefined
          ? { grain: Math.max(step ?? 0, domain.granularity ?? 0) }
          : {}),
        pct,
        format: domain.format,
      })
    : undefined;

  // A continuous count is INTERPOLATED whenever the handle cuts a bucket: it assumes rows are spread
  // evenly inside that bucket, which on a skewed column they are not. It is also inexact when the frame
  // omits rows (the numeric histogram is framed at p01/p99, so the tails sit outside it) or when the
  // bins were never measured at all. Any of those ⇒ say "About", rather than printing a measurement.
  const partialBin =
    !isPointOp &&
    bins.some((bin) => {
      const fraction = activeFraction(bin);
      return fraction > 0 && fraction < 1;
    });
  const framedOut = total !== undefined && total > binsTotal;
  const interpolated = estimated || partialBin || framedOut;

  const selectedCount = point
    ? point.count
    : Math.round(bins.reduce((sum, bin) => sum + bin.count * activeFraction(bin), 0));
  const hardSegments = point ? point.segments : slider.segments;

  // It matters that this is density and not raw count when the bins are NOT equal-width: the recency
  // encoding clamps its newest bin at zero days-ago, leaving one roughly half the width of its
  // neighbours, and a count-scaled bar would draw those 942 rows at a quarter of a neighbour's area.
  const boxes = densityBarBoxes(
    bins.map((bin) => ({ from: bin.start, to: bin.end, mass: bin.count })),
    pct,
    HISTOGRAM_MIN_BAR_PCT,
  );
  const bars = (fill: string) =>
    bins.map((_, i) => <div key={i} className={cn('absolute bottom-0 rounded-sm', fill)} style={boxes[i]} />);

  // Every quartile is marked as a tick and offered as a label; `placeAxisLabels` keeps as many as fit,
  // in rank order. p01/p99 are deliberately NOT offered as interior landmarks: when the frame was cut at
  // them they ARE the ends, already named there, and a second copy flush against the edge would only be
  // dropped. When the frame was not cut at them they sit outside it and cannot be drawn at all.
  //
  // On a LOG axis the quartiles cluster and say nothing about where the reader is; the magnitude ladder
  // does. On a linear axis the quartiles are still the best ticks there are. The landmark LABELS are
  // unchanged either way — only which positions get a tick mark.
  const axisTicks =
    scale === 'linear'
      ? [
          // The ends carry ticks too — once named they are landmarks in their own right, not just the frame.
          0,
          100,
          ...(stats
            ? [stats.p25, stats.p50, stats.p75, stats.p95]
                .map((v) => pct(clampValue(v)))
                .filter((p) => p > 0 && p < 100)
            : []),
        ]
      : [
          0,
          100,
          ...logTickValues(min, max, 6)
            .map((v) => pct(v))
            .filter((p) => p > 0 && p < 100),
        ];
  const landmarks = (
    stats
      ? [
          { v: stats.p50, label: 'Median' },
          { v: stats.p95, label: 'Top 5%' },
          { v: stats.p75, label: 'Top 25%' },
          { v: stats.p25, label: 'Bottom 25%' },
        ]
      : []
  ).map((m) => ({
    pos: pct(clampValue(m.v)),
    label: m.label,
    value: domain.format(m.v),
  }));

  // NAME the ends. The frame is whatever edges the probe was given — conventionally p01/p99, so an end
  // is usually a percentile CUT rather than the column's extreme, and a bare number there says nothing
  // about which. Only a level that demonstrably EQUALS the edge earns the name: the convention is not
  // enforced anywhere, so an end captioned `Bottom 1%` that is really the minimum would be exactly the
  // kind of untrue label this ticket is about. `Lowest`/`Highest` win the tie — reaching the real
  // extreme says more.
  const endName = (edge: number, extreme: number | undefined, tail: number | undefined, names: [string, string]) =>
    extreme === edge ? names[0] : tail === edge ? names[1] : undefined;
  const minEnd = {
    value: domain.format(min),
    ...optName(endName(min, stats?.min, stats?.p01, ['Lowest', 'Bottom 1%'])),
  };
  const maxEnd = {
    value: domain.format(max),
    ...optName(endName(max, stats?.max, stats?.p99, ['Highest', 'Top 1%'])),
  };
  // A bound states where the truth cannot be, which says nothing about how well the underlying bins are
  // known. Quoting "Up to 491" over pg_stats-interpolated bins hid that entirely, because the summary
  // line shows a bound OR an estimate and never both — so the estimate is disclosed here instead.
  const caption =
    [committed && point ? point.note : undefined, committed && point && estimated ? BINS_INTERPOLATED : undefined]
      .filter(Boolean)
      .join(' ') || undefined;

  return (
    <SliderShell
      slider={slider}
      summary={
        <InsightSummary
          matchCount={selectedCount}
          total={total}
          bound={point?.bound}
          estimated={interpolated}
          unset={!committed}
          unsetLabel="No threshold set"
        />
      }
      ticks={axisTicks}
      landmarks={landmarks}
      minLabel={minEnd}
      maxLabel={maxEnd}
      segments={hardSegments}
      softSegments={point?.soft}
      guideStyle={isPointOp ? 'solid' : 'dashed'}
      caption={caption}
    >
      {bins.map((bin, i) => (
        <div
          key={i}
          // `about` on interpolated bins: the count is derived from table statistics, and a bare figure
          // in a tooltip reads as measured exactly like the summary line did before it was qualified.
          title={`${domain.format(bin.start)}–${domain.format(bin.end)} · ${estimated ? 'about ' : ''}${formatCount(bin.count)} rows`}
          className="absolute bottom-0 rounded-sm bg-grey-100"
          style={boxes[i]}
        />
      ))}
      {/* The bucket that merely CONTAINS an eq/neq value: muted, so it never reads as a set of matches. */}
      {committed && point?.soft && <SpanFill spans={point.soft}>{bars('bg-primary/30')}</SpanFill>}
      {/* Definite matches, clipped to the selected span(s) so a boundary bin is coloured only up to the
          handle and the highlighted area equals the quoted count. */}
      {committed && <SpanFill spans={hardSegments}>{bars('bg-primary')}</SpanFill>}
    </SliderShell>
  );
}
